import type { UpdateTrailerInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { activateTrailer, deactivateTrailer, updateTrailer } from './trailers.api';
import { useTrailerRecord } from './use-trailer-record';

/** A trailer's own page: its record (`useTrailerRecord`) plus every mutation the Details tab needs. */
export function useTrailerDetail(id: string) {
  const { state, reload } = useTrailerRecord(id);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function update(input: UpdateTrailerInput): Promise<boolean> {
    setUpdateError(null);
    setSaving(true);
    try {
      await updateTrailer(id, input);
      await reload();
      toast.success('Trailer updated');
      return true;
    } catch (err) {
      setUpdateError(
        err instanceof ApiError && err.status === 409 ? 'A trailer with this registration already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  /** A row action with no form of its own: reload and toast on success, toast.error on failure (CLAUDE.md → Web conventions). */
  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  const activate = () => rowAction(() => activateTrailer(id), 'Trailer activated', "Couldn't activate the trailer");
  const deactivate = () => rowAction(() => deactivateTrailer(id), 'Trailer deactivated', "Couldn't deactivate the trailer");

  return { state, updateError, saving, update, activate, deactivate };
}
